<template>
  <div class="pp-analytics" :style="{ '--pp-color': companyColor }">
    <header class="pp-analytics__bar">
      <button type="button" class="pp-back" @click="goEditor">
        <chevron-left-icon size="18" /> Voltar ao editor
      </button>
      <h1>Analytics do perfil <pro-badge /></h1>
      <div class="pp-analytics__periods" role="group" aria-label="Período">
        <button
          v-for="option in periods"
          :key="option"
          type="button"
          :class="{ active: days === option }"
          @click="setDays(option)"
        >
          {{ option }} dias
        </button>
      </div>
    </header>

    <main class="pp-analytics__body">
      <p v-if="loading && !report" class="pp-analytics__muted">Carregando...</p>
      <div v-else-if="error" class="pp-analytics__card">
        <p class="pp-analytics__muted">Não conseguimos carregar agora.</p>
        <button type="button" class="pp-btn" @click="retryLoad">Tentar de novo</button>
      </div>

      <!-- Grátis: só o total, e o resto borrado para dar vontade de ver. -->
      <template v-else-if="report && report.locked">
        <section class="pp-analytics__card pp-analytics__teaser">
          <p class="pp-analytics__big">{{ report.totals.visits }}</p>
          <p>
            {{ report.totals.visits === 1 ? "visita" : "visitas" }} no seu perfil nos últimos
            {{ days }} dias
          </p>
          <p class="pp-analytics__muted">
            Com o PRO você vê quem veio de onde (WhatsApp, LinkedIn, e-mail), quanto tempo ficaram,
            quem baixou seu currículo e o resultado de cada link que você mandar.
          </p>
          <button type="button" class="pp-btn pp-btn--primary" @click="openPaywall">
            Ver tudo com o PRO
          </button>
        </section>
        <div class="pp-analytics__blur" aria-hidden="true">
          <div class="pp-analytics__kpis">
            <div v-for="n in 4" :key="n" class="pp-analytics__kpi">
              <span>Métrica</span><strong>--</strong>
            </div>
          </div>
          <div class="pp-analytics__card pp-analytics__fake-chart" />
        </div>
      </template>

      <template v-else-if="report">
        <div class="pp-analytics__kpis">
          <div v-for="kpi in kpis" :key="kpi.label" class="pp-analytics__kpi">
            <span>{{ kpi.label }}</span>
            <strong>{{ kpi.value }}</strong>
            <small v-if="kpi.delta" :class="`pp-analytics__delta--${kpi.delta.tone}`">{{ kpi.delta.text }}</small>
          </div>
        </div>

        <section class="pp-analytics__card">
          <h2>Visitas por dia</h2>
          <div class="pp-analytics__chart" :aria-label="`Visitas nos últimos ${days} dias`">
            <div
              v-for="point in report.daily"
              :key="point.date"
              class="pp-analytics__bar-col"
              :title="`${formatDay(point.date)}: ${point.visits} ${point.visits === 1 ? 'visita' : 'visitas'}`"
            >
              <span class="pp-analytics__bar-fill" :style="{ height: `${barHeight(point.visits)}%` }" />
            </div>
          </div>
          <div class="pp-analytics__axis">
            <span>{{ formatDay(report.period.from) }}</span>
            <span>{{ formatDay(report.period.to) }}</span>
          </div>
        </section>

        <div class="pp-analytics__grid">
          <section class="pp-analytics__card">
            <h2>De onde vieram</h2>
            <p v-if="!report.sources.length" class="pp-analytics__muted">Nenhuma visita no período.</p>
            <ul class="pp-analytics__ranking">
              <li v-for="item in report.sources" :key="item.source">
                <span class="pp-analytics__rank-label">{{ item.source }}</span>
                <span class="pp-analytics__rank-bar">
                  <i :style="{ width: `${share(item.visits, report.totals.visits)}%` }" />
                </span>
                <span class="pp-analytics__rank-value">{{ item.visits }}</span>
              </li>
            </ul>
            <p class="pp-analytics__note">
              WhatsApp e apps de mensagem nem sempre dizem de onde a pessoa veio. Use um link
              rastreado (abaixo) para saber.
            </p>
          </section>

          <section class="pp-analytics__card">
            <h2>Dispositivos</h2>
            <ul class="pp-analytics__ranking">
              <li v-for="item in report.devices" :key="item.device">
                <span class="pp-analytics__rank-label">{{ deviceLabel(item.device) }}</span>
                <span class="pp-analytics__rank-bar">
                  <i :style="{ width: `${share(item.visits, report.totals.visits)}%` }" />
                </span>
                <span class="pp-analytics__rank-value">{{ share(item.visits, report.totals.visits) }}%</span>
              </li>
            </ul>
            <template v-if="report.links.length">
              <h2 class="mt-6">Cliques nos seus links</h2>
              <ul class="pp-analytics__ranking">
                <li v-for="item in report.links" :key="item.target">
                  <span class="pp-analytics__rank-label">{{ item.target }}</span>
                  <span class="pp-analytics__rank-value">{{ item.clicks }}</span>
                </li>
              </ul>
            </template>
          </section>
        </div>

        <section class="pp-analytics__card">
          <h2>Link rastreado</h2>
          <p class="pp-analytics__muted">
            Vai mandar o perfil num grupo, num e-mail ou numa candidatura? Crie um link para cada
            lugar e veja aqui qual deu resultado.
          </p>
          <div class="pp-analytics__builder">
            <div class="pp-analytics__field">
              <span>Onde vai mandar</span>
              <!-- Mesmo v-select das outras telas do app (Vacancy.vue), não o select do navegador. -->
              <v-select
                v-model="utm.source"
                :options="sourceOptions"
                :reduce="(option) => option.value"
                :clearable="false"
                :searchable="false"
                class="pp-vselect"
              />
            </div>
            <label>
              Nome para você reconhecer
              <input
                v-model="utm.campaign"
                class="pp-input"
                maxlength="60"
                placeholder="Ex.: grupo-vagas-bh"
                @input="utm.campaign = slugify(utm.campaign)"
              />
            </label>
          </div>
          <div class="pp-analytics__link">
            <span>{{ trackedLink }}</span>
            <button type="button" class="pp-btn pp-btn--primary pp-btn--small" @click="copyLink">
              {{ copied ? "Copiado" : "Copiar link" }}
            </button>
          </div>
        </section>

        <section class="pp-analytics__card">
          <h2>Campanhas</h2>
          <p v-if="!report.campaigns.length" class="pp-analytics__muted">
            Ainda sem visitas por link rastreado. Quem entende de UTM pode usar os próprios
            parâmetros (utm_source, utm_medium, utm_campaign): eles aparecem aqui.
          </p>
          <table v-else class="pp-analytics__table">
            <thead>
              <tr>
                <th>Origem</th>
                <th>Meio</th>
                <th>Campanha</th>
                <th class="num">Visitas</th>
                <th class="num">Tempo médio</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in report.campaigns" :key="index">
                <td>{{ row.source || "-" }}</td>
                <td>{{ row.medium || "-" }}</td>
                <td>{{ row.campaign || "-" }}</td>
                <td class="num">{{ row.visits }}</td>
                <td class="num">{{ duration(row.avg_seconds) }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section class="pp-analytics__card">
          <h2>Últimas visitas</h2>
          <p v-if="!report.recent.length" class="pp-analytics__muted">Ninguém abriu seu perfil ainda.</p>
          <ul class="pp-analytics__recent">
            <li v-for="(visit, index) in report.recent" :key="index">
              <span class="pp-analytics__recent-when">{{ timeAgo(visit.started_at) }}</span>
              <span>
                <strong>{{ visit.source }}</strong>
                <template v-if="visit.campaign"> · {{ visit.campaign }}</template>
              </span>
              <span class="pp-analytics__muted">
                {{ deviceLabel(visit.device) }} · {{ duration(visit.duration_seconds) }}
              </span>
              <span class="pp-analytics__tags">
                <em v-for="action in visit.actions" :key="action">{{ actionLabel(action) }}</em>
              </span>
            </li>
          </ul>
        </section>

        <p class="pp-analytics__note">
          Não usamos cookies: contamos visitas sem identificar quem visitou. Suas próprias visitas ao
          link também entram na conta.
        </p>
      </template>
    </main>

    <paywall-dialog :active.sync="paywall" source="analytics" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ChevronLeftIcon } from "vue-feather-icons";
import vSelect from "vue-select";
import PaywallDialog from "@/components/public-profile/PaywallDialog.vue";
import ProBadge from "@/components/public-profile/ProBadge.vue";
import mixin from "@/components/public-profile/mixin";
import { copyText } from "@/components/public-profile/constants";
import service, { errorReason, errorStatus } from "@/services/publicProfile";

const SOURCE_OPTIONS = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "email", label: "E-mail" },
  { value: "instagram", label: "Instagram" },
  { value: "telegram", label: "Telegram" },
  { value: "candidatura", label: "Candidatura em vaga" },
  { value: "outro", label: "Outro lugar" },
];

const ACTIONS = {
  resume_download: "Baixou o currículo",
  contact_open: "Abriu o contato",
  message_sent: "Mandou mensagem",
  link_click: "Clicou num link",
  video_play: "Viu o vídeo",
  audio_play: "Ouviu o áudio",
  share: "Compartilhou",
};

export default {
  name: "PublicProfileAnalytics",
  components: { ChevronLeftIcon, PaywallDialog, ProBadge, vSelect },
  mixins: [mixin],
  data: () => ({
    days: 30,
    periods: [7, 30, 90],
    report: null,
    loading: false,
    error: false,
    paywall: false,
    copied: false,
    utm: { source: "whatsapp", campaign: "" },
  }),
  computed: {
    ...mapGetters("publicProfile", ["page"]),
    sourceOptions: () => SOURCE_OPTIONS,
    kpis() {
      const t = this.report.totals;
      const p = this.report.previous || {};
      return [
        { label: "Visitas", value: t.visits, delta: this.delta(t.visits, p.visits) },
        { label: "Visitantes únicos", value: t.visitors, delta: this.delta(t.visitors, p.visitors) },
        { label: "Tempo médio", value: this.duration(t.avg_seconds) },
        { label: "Baixaram o currículo", value: t.resume_downloads },
        { label: "Mensagens recebidas", value: t.messages },
        { label: "Cliques nos links", value: t.link_clicks },
      ];
    },
    maxDaily() {
      return Math.max(1, ...this.report.daily.map((point) => point.visits));
    },
    trackedLink() {
      const base = (this.page && this.page.url) || "";
      if (!base) return "";
      const params = new URLSearchParams({ utm_source: this.utm.source, utm_medium: "link" });
      if (this.utm.campaign) params.set("utm_campaign", this.utm.campaign);
      return `${base}?${params.toString()}`;
    },
  },
  watch: {
    "utm.source"(value, from) {
      this.ppTrack("public_profile_tracked_link_source_changed", { utm_source: value, from });
    },
  },
  created() {
    this.openedAt = Date.now();
    if (!this.page) this.$store.dispatch("publicProfile/fetch").catch(() => {});
    this.load();
  },
  beforeDestroy() {
    this.ppTrack("public_profile_analytics_closed", {
      seconds_open: Math.round((Date.now() - this.openedAt) / 1000),
      days: this.days,
      locked: !!(this.report && this.report.locked),
    });
  },
  methods: {
    async load() {
      // Trocou de periodo antes de a resposta chegar: so a ultima conta.
      this.requestId = (this.requestId || 0) + 1;
      const requestId = this.requestId;
      this.loading = true;
      this.error = false;
      try {
        const report = await service.analytics(this.days);
        if (requestId === this.requestId) {
          this.report = report;
          this.trackReport(report);
        }
      } catch (e) {
        if (requestId === this.requestId) {
          this.error = true;
          this.ppTrack("public_profile_analytics_load_failed", { days: this.days, status: errorStatus(e), reason: errorReason(e) });
        }
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    // Um evento por relatório carregado, com os números que a pessoa viu.
    trackReport(report) {
      const totals = (report && report.totals) || {};
      this.ppTrack("public_profile_analytics_viewed", {
        days: this.days,
        locked: !!report.locked,
        first_load: !this.viewedOnce,
        visits: totals.visits !== undefined ? totals.visits : null,
        visitors: totals.visitors !== undefined ? totals.visitors : null,
        avg_seconds: totals.avg_seconds !== undefined ? totals.avg_seconds : null,
        resume_downloads: totals.resume_downloads !== undefined ? totals.resume_downloads : null,
        messages: totals.messages !== undefined ? totals.messages : null,
        link_clicks: totals.link_clicks !== undefined ? totals.link_clicks : null,
      });
      this.viewedOnce = true;
    },
    retryLoad() {
      this.ppTrack("public_profile_analytics_load_retry_clicked", { days: this.days });
      this.load();
    },
    openPaywall() {
      this.ppTrack("public_profile_pro_cta_clicked", {
        cta: "analytics_locked",
        visits: this.report && this.report.totals ? this.report.totals.visits : null,
      });
      this.paywall = true;
    },
    setDays(days) {
      if (days !== this.days) this.ppTrack("public_profile_analytics_period_changed", { days, from: this.days });
      this.days = days;
      this.load();
    },
    goEditor() {
      this.ppTrack("public_profile_analytics_back_clicked");
      this.$router.push({ name: "public-profile-editor", params: { company_label: this.$route.params.company_label } });
    },
    barHeight(value) {
      return value ? Math.max(4, Math.round((value / this.maxDaily) * 100)) : 0;
    },
    share(value, total) {
      return total ? Math.round((value / total) * 100) : 0;
    },
    delta(current, previous) {
      if (previous === undefined || previous === null) return null;
      if (!previous) return current ? { tone: "up", text: "Novo no período" } : null;
      const change = Math.round(((current - previous) / previous) * 100);
      if (!change) return { tone: "flat", text: "Igual ao período anterior" };
      return {
        tone: change > 0 ? "up" : "down",
        text: `${change > 0 ? "+" : ""}${change}% vs. período anterior`,
      };
    },
    duration(seconds) {
      const value = Number(seconds || 0);
      if (!value) return "0 s";
      const minutes = Math.floor(value / 60);
      const rest = value % 60;
      return minutes ? `${minutes} min ${String(rest).padStart(2, "0")} s` : `${rest} s`;
    },
    deviceLabel(device) {
      return { mobile: "Celular", desktop: "Computador", tablet: "Tablet" }[device] || device;
    },
    actionLabel(action) {
      return ACTIONS[action] || action;
    },
    formatDay(value) {
      const [year, month, day] = String(value).slice(0, 10).split("-");
      return year ? `${day}/${month}` : "";
    },
    timeAgo(iso) {
      const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
      if (minutes < 1) return "agora";
      if (minutes < 60) return `há ${minutes} min`;
      const hours = Math.round(minutes / 60);
      if (hours < 24) return `há ${hours} h`;
      const days = Math.round(hours / 24);
      return days === 1 ? "ontem" : `há ${days} dias`;
    },
    slugify(text) {
      return String(text || "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9-]+/g, "-")
        .replace(/-{2,}/g, "-");
    },
    async copyLink() {
      if (!this.trackedLink) return;
      const ok = await copyText(this.trackedLink);
      if (!ok) {
        this.ppError("Não deu para copiar. Selecione o link e copie.");
        return;
      }
      this.copied = true;
      this.ppTrack("public_profile_tracked_link_copied", {
        source: this.utm.source,
        utm_source: this.utm.source,
        utm_campaign: this.utm.campaign || null,
        link: this.trackedLink,
      });
      setTimeout(() => {
        this.copied = false;
      }, 2000);
    },
  },
};
</script>

<style lang="scss">
.pp-analytics {
  min-height: 100vh;
  background: #f2f4f7;
  font-family: Roboto, sans-serif;
  color: #333;

  &__bar {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 24px;
    background: #fff;
    border-bottom: 0.5px solid #d9d9d9;

    h1 {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }
  }

  &__periods {
    display: flex;
    gap: 4px;
    padding: 3px;
    border-radius: 8px;
    background: #f2f4f7;

    button {
      padding: 6px 12px;
      border: none;
      border-radius: 6px;
      background: none;
      color: #4f4f4f;
      font-family: inherit;
      font-size: 13px;
      cursor: pointer;

      &.active {
        background: #fff;
        color: #333;
        font-weight: 500;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
      }
    }
  }

  &__body {
    max-width: 1080px;
    margin: 0 auto;
    padding: 24px 16px 48px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__card {
    padding: 20px 24px;
    border-radius: 16px;
    background: #fff;

    h2 {
      margin: 0 0 12px;
      font-size: 16px;
      font-weight: 600;
    }
  }

  &__muted {
    margin: 0 0 12px;
    color: #696969;
    font-size: 14px;
    line-height: 1.5;
  }

  &__note {
    margin: 12px 0 0;
    color: #696969;
    font-size: 12px;
    line-height: 1.5;
  }

  &__kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
  }

  &__kpi {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 16px;
    border-radius: 16px;
    background: #fff;

    span {
      color: #696969;
      font-size: 13px;
    }

    strong {
      font-size: 28px;
      font-weight: 600;
      color: #06152b;
    }

    small {
      font-size: 12px;
    }
  }

  &__delta--up {
    color: #309e3a;
  }

  &__delta--down {
    color: #de2348;
  }

  &__delta--flat {
    color: #696969;
  }

  &__chart {
    display: flex;
    align-items: flex-end;
    gap: 3px;
    height: 160px;
    padding-bottom: 4px;
    border-bottom: 0.5px solid #d9d9d9;
  }

  &__bar-col {
    flex: 1;
    height: 100%;
    display: flex;
    align-items: flex-end;
  }

  &__bar-fill {
    width: 100%;
    border-radius: 4px 4px 0 0;
    background: var(--pp-color);
  }

  &__axis {
    display: flex;
    justify-content: space-between;
    margin-top: 6px;
    color: #696969;
    font-size: 12px;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  &__ranking {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 10px;

    li {
      display: grid;
      grid-template-columns: 130px 1fr 44px;
      align-items: center;
      gap: 12px;
      font-size: 14px;
    }
  }

  &__rank-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__rank-bar {
    height: 8px;
    border-radius: 9999px;
    background: #f2f4f7;

    i {
      display: block;
      height: 100%;
      border-radius: 9999px;
      background: var(--pp-color);
    }
  }

  &__rank-value {
    text-align: right;
    font-weight: 500;
  }

  &__builder {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;

    label,
    .pp-analytics__field {
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 14px;
    }
  }

  &__link {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    padding: 8px 8px 8px 14px;
    border: 0.5px solid #a8a8a8;
    border-radius: 8px;

    span {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #4f4f4f;
      font-size: 14px;
    }
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;

    th {
      padding: 0 8px 8px 0;
      border-bottom: 0.5px solid #d9d9d9;
      color: #696969;
      font-weight: 500;
      text-align: left;
    }

    td {
      padding: 10px 8px 10px 0;
      border-bottom: 0.5px solid #f2f4f7;
    }

    .num {
      text-align: right;
    }
  }

  &__recent {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      display: grid;
      grid-template-columns: 90px 1fr auto;
      gap: 4px 16px;
      padding: 10px 0;
      border-bottom: 0.5px solid #f2f4f7;
      font-size: 14px;

      .pp-analytics__muted {
        margin: 0;
      }
    }
  }

  &__recent-when {
    color: #696969;
  }

  &__tags {
    grid-column: 2 / -1;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    em {
      padding: 2px 8px;
      border-radius: 9999px;
      background: #f2f4f7;
      color: #4f4f4f;
      font-size: 12px;
      font-style: normal;
    }

    &:empty {
      display: none;
    }
  }

  &__teaser {
    text-align: center;

    p {
      margin: 0 0 8px;
    }

    .pp-btn {
      margin-top: 8px;
    }
  }

  &__big {
    font-size: 56px;
    font-weight: 600;
    color: #06152b;
  }

  &__blur {
    display: flex;
    flex-direction: column;
    gap: 16px;
    filter: blur(6px);
    opacity: 0.6;
    pointer-events: none;
  }

  &__fake-chart {
    height: 220px;
    background: linear-gradient(0deg, #e7edf0 0 30%, transparent 30%), #fff;
  }

  @media (max-width: 768px) {
    &__bar {
      flex-wrap: wrap;
      padding: 12px 16px;

      h1 {
        order: 3;
        flex-basis: 100%;
      }
    }

    &__grid,
    &__builder {
      grid-template-columns: 1fr;
    }

    &__ranking li {
      grid-template-columns: 100px 1fr 40px;
    }

    &__table {
      display: block;
      overflow-x: auto;
    }

    &__recent li {
      grid-template-columns: 1fr;
    }
  }
}

.pp-vselect {
  font-family: Roboto, sans-serif;
  font-size: 14px;
  color: #333;

  .vs__dropdown-toggle {
    min-height: 40px;
    padding: 0 8px 0 4px;
    border: 0.5px solid #a8a8a8;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: var(--pp-color);
    box-shadow: 0 0 0 3px rgba(21, 81, 177, 0.12);
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  .vs__selected {
    margin: 4px 2px 0 6px;
    color: #333;
    font-weight: 500;
  }

  .vs__search,
  .vs__search:focus {
    margin: 0;
    padding: 0;
    cursor: pointer;
  }

  .vs__actions {
    padding: 0 2px;
    color: #696969;
  }

  .vs__dropdown-menu {
    margin-top: 4px;
    padding: 6px;
    border: 0.5px solid #d9d9d9;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(6, 21, 43, 0.12);
  }

  .vs__dropdown-option {
    padding: 8px 10px;
    border-radius: 6px;
    color: #333;
  }

  .vs__dropdown-option--highlight {
    background: #f2f4f7;
    color: #333;
  }

  .vs__dropdown-option--selected {
    color: var(--pp-color);
    font-weight: 500;
  }
}
</style>
